import React, { useState, useCallback, useRef, useEffect } from 'react';
import usePersistentState from '../utils/usePersistentState';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LabelList,
} from 'recharts';
import { reportAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Spinner, Button, SearchableSelect } from '../components/UI';
import SubmissionJourney from '../components/SubmissionJourney';

// ── Colour palettes (dark / light paired) ──────────────────────────────────────
// Dark: bright accent shades that glow on deep slate.
// Light: saturated 600-level shades that carry weight on white.
const STATUS_COLORS_DARK = {
  Applied:     '#6366f1',
  Shortlisted: '#f59e0b',
  Interview:   '#3b82f6',
  Hired:       '#10b981',
  Rejected:    '#ef4444',
};
const STATUS_COLORS_LIGHT = {
  Applied:     '#4f46e5',
  Shortlisted: '#d97706',
  Interview:   '#2563eb',
  Hired:       '#059669',
  Rejected:    '#dc2626',
};
const PIE_COLORS_DARK  = ['#6366f1','#10b981','#f59e0b','#3b82f6','#ef4444','#8b5cf6','#ec4899'];
const PIE_COLORS_LIGHT = ['#4f46e5','#059669','#d97706','#2563eb','#dc2626','#7c3aed','#be185d'];

// Legacy aliases (used by swatches / pills rendered in JSX — these spots are
// ALSO covered by the light-override.css layer so they keep legibility on
// either background).
const STATUS_COLORS = STATUS_COLORS_DARK;
const PIE_COLORS    = PIE_COLORS_DARK;

// ── Small helpers ─────────────────────────────────────────────────────────────
const localDateStr = (d) => {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const today      = () => localDateStr(new Date());
const daysAgo    = (n) => localDateStr(new Date(Date.now() - n * 86400000));
const fmtDate    = (d) => new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
const pct        = (n, t) => t ? `${((n / t) * 100).toFixed(1)}%` : '0%';

// ── Print utility ─────────────────────────────────────────────────────────────
// Opens a clean, light-themed print window with a formatted table.
// columns: [{ label, key, width? }]
// rows:    array of objects
const STATUS_BADGE_COLORS = {
  Applied:     '#4f46e5',
  Shortlisted: '#d97706',
  Interview:   '#2563eb',
  Hired:       '#059669',
  Rejected:    '#dc2626',
};

function printCandidateList({ title, subtitle, dateRange, columns, rows }) {
  const badgeStyle = (status) => {
    const color = STATUS_BADGE_COLORS[status] || '#6b7280';
    return `display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:600;color:#fff;background:${color}`;
  };

  const thead = columns.map(c => `<th style="text-align:left;padding:8px 12px;border-bottom:2px solid #e5e7eb;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;white-space:nowrap;${c.width ? `width:${c.width}` : ''}">${c.label}</th>`).join('');

  const tbody = rows.map((row, i) => {
    const cells = columns.map(c => {
      const val = row[c.key] ?? '—';
      const cell = c.key === 'status'
        ? `<span style="${badgeStyle(val)}">${val}</span>`
        : `<span style="color:#111827">${val}</span>`;
      return `<td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;font-size:12px;vertical-align:middle">${cell}</td>`;
    }).join('');
    const bg = i % 2 === 0 ? '#ffffff' : '#f9fafb';
    return `<tr style="background:${bg}">${cells}</tr>`;
  }).join('');

  const now = new Date().toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #fff; color: #111827; padding: 32px; }
    .header { border-bottom: 3px solid #4f46e5; padding-bottom: 16px; margin-bottom: 24px; }
    .brand { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .1em; color: #4f46e5; margin-bottom: 6px; }
    h1 { font-size: 22px; font-weight: 700; color: #111827; }
    .meta { margin-top: 6px; font-size: 12px; color: #6b7280; display: flex; gap: 24px; flex-wrap: wrap; }
    .meta span b { color: #374151; }
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    .summary { display: flex; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
    .stat { background: #f3f4f6; border-radius: 8px; padding: 10px 18px; text-align: center; }
    .stat-val { font-size: 20px; font-weight: 700; color: #4f46e5; }
    .stat-lbl { font-size: 11px; color: #6b7280; margin-top: 2px; }
    .footer { margin-top: 24px; font-size: 11px; color: #9ca3af; text-align: right; border-top: 1px solid #e5e7eb; padding-top: 10px; }
    @media print {
      body { padding: 16px; }
      @page { margin: 14mm; size: A4 landscape; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="brand">RecruitPro · Recruitment Management System</div>
    <h1>${title}</h1>
    <div class="meta">
      ${subtitle ? `<span>${subtitle}</span>` : ''}
      ${dateRange ? `<span><b>Period:</b> ${dateRange}</span>` : ''}
      <span><b>Total records:</b> ${rows.length}</span>
      <span><b>Printed:</b> ${now}</span>
    </div>
  </div>

  <div class="summary">
    ${['Applied','Screening','Shortlisted','Interview','Hold','Dropped','Hired','Rejected'].map(s => {
      const cnt = rows.filter(r => r.status === s).length;
      return cnt > 0
        ? `<div class="stat"><div class="stat-val" style="color:${STATUS_BADGE_COLORS[s]}">${cnt}</div><div class="stat-lbl">${s}</div></div>`
        : '';
    }).join('')}
  </div>

  <table>
    <thead><tr style="background:#f9fafb">${thead}</tr></thead>
    <tbody>${tbody}</tbody>
  </table>

  <div class="footer">Generated by RecruitPro Reports · ${now}</div>
  <script>window.onload = function() { window.print(); }<\/script>
</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}

// ── Reusable print button ──────────────────────────────────────────────────────
function PrintButton({ onClick, label = 'Print' }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-700/60 border border-slate-600/50 text-white hover:bg-slate-600 hover:text-white transition-all"
    >
      🖨️ {label}
    </button>
  );
}

const QUICK = [
  { label: 'Today',        from: today(),       to: today()      },
  { label: 'Last 7 days',  from: daysAgo(7),    to: today()      },
  { label: 'This Month',   from: today().slice(0,7)+'-01', to: today() },
  { label: 'Last 30 days', from: daysAgo(30),   to: today()      },
  { label: 'Last 90 days', from: daysAgo(90),   to: today()      },
];

// ── Reusable card ─────────────────────────────────────────────────────────────
function Card({ title, sub, headerRight, children, className = '' }) {
  return (
    <div className={`rounded-xl p-5 ${className}`} style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border-subtle)',
    }}>
      {(title || headerRight) && (
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            {title && <h3 style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>{title}</h3>}
            {sub && <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 3 }}>{sub}</p>}
          </div>
          {headerRight && <div className="flex-shrink-0">{headerRight}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

// ── Status pill ───────────────────────────────────────────────────────────────
function StatusBadge({ s }) {
  const BADGE = {
    Applied:     { bg: 'rgba(99,102,241,0.15)',  color: '#818cf8', border: 'rgba(99,102,241,0.3)'  },
    Shortlisted: { bg: 'rgba(245,158,11,0.15)',  color: '#fbbf24', border: 'rgba(245,158,11,0.3)'  },
    Interview:   { bg: 'rgba(59,130,246,0.15)',  color: '#60a5fa', border: 'rgba(59,130,246,0.3)'  },
    Hired:       { bg: 'rgba(16,185,129,0.15)',  color: '#34d399', border: 'rgba(16,185,129,0.3)'  },
    Rejected:    { bg: 'rgba(239,68,68,0.15)',   color: '#f87171', border: 'rgba(239,68,68,0.3)'   },
  };
  const b = BADGE[s] || { bg: 'rgba(100,116,139,0.15)', color: '#94a3b8', border: 'rgba(100,116,139,0.3)' };
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 99,
      background: b.bg, color: b.color, border: `1px solid ${b.border}`,
      fontWeight: 600,
    }}>{s}</span>
  );
}

// ── Mini horizontal bar ───────────────────────────────────────────────────────
function MiniBar({ value, max, color }) {
  const w = max ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 rounded-full h-1.5" style={{ background: 'var(--border-subtle)' }}>
        <div className="h-1.5 rounded-full transition-all" style={{ width: `${w}%`, background: color }} />
      </div>
      <span className="text-xs w-6 text-right" style={{ color: 'var(--text-secondary)' }}>{value}</span>
    </div>
  );
}

// ── Requirement candidate drill-down (table expanded row) ────────────────────
function RequirementCandidatesRow({ row, colSpan }) {
  const [candStatus, setCandStatus] = useState('All');

  const filtered = candStatus === 'All'
    ? (row.candidates || [])
    : (row.candidates || []).filter(c => c.status === candStatus);

  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        <div className="bg-slate-900/50 border-t border-slate-700/50 px-4 py-4 space-y-3">

          {/* Status filter tabs + count */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Candidates for <span className="text-white">{row.jobTitle}</span> ({filtered.length})
            </span>
            <div className="flex gap-1 flex-wrap">
              {['All', 'Applied', 'Screening', 'Shortlisted', 'Interview', 'Hold', 'Dropped', 'Hired', 'Rejected'].map(s => (
                <button
                  key={s}
                  onClick={() => setCandStatus(s)}
                  className={`text-xs px-2 py-0.5 rounded-full border transition-all ${
                    candStatus === s
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-slate-700/40 border-slate-600/50 text-white hover:text-white'
                  }`}
                >
                  {s}
                  {s !== 'All' && (
                    <span className="ml-1 opacity-60">{row.byStatus[s] || 0}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Candidate table */}
          {filtered.length === 0 ? (
            <p className="text-slate-500 text-xs text-center py-4">
              No candidates with status "{candStatus}"
            </p>
          ) : (
            <>
              <div className="flex justify-end mb-2">
                <PrintButton
                  onClick={() => printCandidateList({
                    title:     `Candidates — ${row.jobTitle}`,
                    subtitle:  `Client: ${row.clientName} · Type: ${row.workMode}${candStatus !== 'All' ? ` · Status: ${candStatus}` : ''}`,
                    columns: [
                      { label: '#',            key: '_idx',        width: '40px'  },
                      { label: 'Candidate',    key: 'fullName'                    },
                      { label: 'Status',       key: 'status',      width: '110px' },
                      { label: 'Submitted By', key: 'submittedBy', width: '140px' },
                      { label: 'Date',         key: '_date',       width: '110px' },
                    ],
                    rows: filtered.map((c, i) => ({ ...c, _idx: i + 1, _date: fmtDate(c.createdAt) })),
                  })}
                />
              </div>
              <div className="rounded-lg border border-slate-700/50 overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500 bg-slate-800/70 border-b border-slate-700/50">
                      <th className="text-left py-2 px-3 whitespace-nowrap">#</th>
                      <th className="text-left py-2 px-3 whitespace-nowrap">Candidate</th>
                      <th className="text-left py-2 px-3 whitespace-nowrap">Status</th>
                      <th className="text-left py-2 px-3 whitespace-nowrap">Submitted By</th>
                      <th className="text-left py-2 px-3 whitespace-nowrap">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, i) => (
                      <tr key={c.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors last:border-0">
                        <td className="py-2 px-3 text-slate-500">{i + 1}</td>
                        <td className="py-2 px-3 font-medium text-white">{c.fullName}</td>
                        <td className="py-2 px-3"><StatusBadge s={c.status} /></td>
                        <td className="py-2 px-3 text-slate-400">{c.submittedBy}</td>
                        <td className="py-2 px-3 text-slate-500">{fmtDate(c.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Funnel bucket predicates ──────────────────────────────────────────────────
// Each returns a function suitable for RecruiterCandidateModal's `filterPredicate`
// prop. Backend guarantees each candidate carries `rounds[]` (sorted asc),
// `highestRoundReached`, and `finalOutcome`.
const FUNNEL_PREDICATES = {
  all:           () => () => true,
  interviewed:   () => (c) => (c.rounds?.length || 0) > 0,
  hired:         () => (c) => c.finalOutcome === 'Hired',
  rejected:      () => (c) => c.finalOutcome === 'Rejected',
  dropped:       () => (c) => c.finalOutcome === 'Dropped',
  rejectedFinal: () => (c) => c.finalOutcome === 'Rejected' && c.lastRoundResult === 'Fail',
  rejectedPre:   () => (c) => c.finalOutcome === 'Rejected' && (!c.rounds || c.rounds.length === 0),
  roundReached:  (n) => (c) => (c.highestRoundReached || 0) >= n,
  roundPassed:   (n) => (c) => (c.rounds || []).some(r => r.round === n && r.result === 'Pass'),
  roundFailed:   (n) => (c) => (c.rounds || []).some(r => r.round === n && r.result === 'Fail'),
  roundPending:  (n) => (c) => (c.rounds || []).some(r => r.round === n && (!r.result || r.result === 'Pending')),
};

// ── Per-round chip strip ──────────────────────────────────────────────────────
// [R1 ✓] [R2 ✓] [R3 ✗] [R4 –] etc. Reads a candidate's `rounds` array as
// returned by the recruiterFunnel endpoint. Green check = Pass, red cross =
// Fail, amber dot = Pending, grey dash = not-reached (only rendered if we
// have the count and it exceeds the trail length — but the trail is already
// sorted so we just render what's there).
function RoundChipStrip({ rounds }) {
  if (!rounds || rounds.length === 0) return null;
  const CHIP = (result) => {
    if (result === 'Pass') return { icon: '✓', color: '#10b981', bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.3)' };
    if (result === 'Fail') return { icon: '✗', color: '#ef4444', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.3)' };
    return                    { icon: '•', color: '#fbbf24', bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.3)' };
  };
  return (
    <div className="flex gap-1 mt-1 flex-wrap">
      {rounds.map(r => {
        const chip = CHIP(r.result);
        return (
          <span
            key={r.round}
            title={`Round ${r.round}${r.result ? ' — ' + r.result : ''}${r.date ? ' · ' + r.date : ''}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: 10, padding: '1px 6px', borderRadius: 99, fontWeight: 600,
              background: chip.bg, color: chip.color, border: `1px solid ${chip.border}`,
              lineHeight: 1.6,
            }}
          >
            <span style={{ opacity: 0.8 }}>R{r.round}</span>
            <span>{chip.icon}</span>
          </span>
        );
      })}
    </div>
  );
}

// ── Recruiter candidate modal ─────────────────────────────────────────────────
// Also used as the funnel bucket drill-down popup. When invoked from the
// Interview Funnel tab a caller passes `filterPredicate`, `title`, `subtitle`,
// and `showStatusTabs={false}` so the modal replaces its status-tab UX with
// a pre-filtered bucket view. Existing status-tab callers pass none of these
// props and get identical behaviour to the pre-drilldown build.
function RecruiterCandidateModal({
  rec,
  onClose,
  initialStatus  = 'All',
  filterPredicate,
  title,
  subtitle,
  showStatusTabs = true,
}) {
  const [candStatus, setCandStatus] = useState(initialStatus);
  const [search, setSearch]         = useState('');
  const [showTop, setShowTop]       = useState(false);
  // Which submission's journey is currently expanded (null = none).
  // Keyed on submissionId — one row per submission, and one candidate may
  // appear multiple times if they have multiple submissions.
  const [expandedId, setExpandedId] = useState(null);
  const scrollRef = useRef(null);

  const filtered = (rec.candidates || []).filter(c => {
    const matchBucket = filterPredicate
      ? filterPredicate(c)
      : (candStatus === 'All' || c.status === candStatus);
    const q = search.toLowerCase();
    const matchSearch = !q
      || (c.fullName   || '').toLowerCase().includes(q)
      || (c.email      || '').toLowerCase().includes(q)
      || (c.requirement|| '').toLowerCase().includes(q)
      || (c.client     || '').toLowerCase().includes(q);
    return matchBucket && matchSearch;
  });

  const handleScroll = () => {
    setShowTop((scrollRef.current?.scrollTop || 0) > 200);
  };

  const scrollToTop = () => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Prevent body scroll while modal open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-slate-900 border border-slate-700/60 rounded-2xl w-full max-w-5xl flex flex-col shadow-2xl"
           style={{ maxHeight: '90vh' }}>

        {/* ── Modal header ── */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-700/50 flex-shrink-0">
          <div className="min-w-0">
            <div className="text-white font-bold text-lg truncate" title={title || rec.name}>
              {title || rec.name}
              <span className="text-slate-400 font-medium text-sm ml-2">({filtered.length})</span>
            </div>
            <div className="text-slate-400 text-sm mt-0.5 truncate">
              {subtitle || `${rec.role} · ${rec.total} total submissions`}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Print */}
            <PrintButton
              onClick={() => printCandidateList({
                title:    title ? `${rec.name} — ${title.replace(`${rec.name} — `, '')}` : `Recruiter Report — ${rec.name}`,
                // In drill-down mode carry the bucket subtitle onto the printed
                // report so the exported list is self-describing; otherwise use
                // the status/search-based subtitle.
                subtitle: filterPredicate
                  ? `${subtitle || 'Filtered bucket'}${search ? ` · Search: "${search}"` : ''}`
                  : `Role: ${rec.role}${candStatus !== 'All' ? ` · Status: ${candStatus}` : ''}${search ? ` · Search: "${search}"` : ''}`,
                columns: [
                  { label: '#',           key: '_idx',        width: '40px'  },
                  { label: 'Candidate',   key: 'fullName'                    },
                  { label: 'Email',       key: 'email',       width: '200px' },
                  { label: 'Status',      key: 'status',      width: '110px' },
                  { label: 'Requirement', key: 'requirement'                 },
                  { label: 'Client',      key: 'client',      width: '160px' },
                  { label: 'Submitted',   key: '_date',       width: '110px' },
                ],
                rows: filtered.map((c, i) => ({ ...c, _idx: i + 1, _date: fmtDate(c.createdAt) })),
              })}
            />
            {/* Close */}
            <Button variant="ghost" size="sm" iconOnly onClick={onClose}>×</Button>
          </div>
        </div>

        {/* ── Status mini bars ── (hidden in bucket drill-down) */}
        {showStatusTabs && (
          <div className="px-6 py-3 border-b border-slate-700/30 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 flex-shrink-0">
            {Object.entries(STATUS_COLORS).map(([s, color]) => (
              <div key={s}>
                <div className="text-xs text-slate-500 mb-0.5">{s} <span className="text-slate-400 font-medium">{rec.byStatus[s] || 0}</span></div>
                <MiniBar value={rec.byStatus[s] || 0} max={rec.total} color={color} />
              </div>
            ))}
          </div>
        )}

        {/* ── Filters ── */}
        <div className="px-6 py-3 border-b border-slate-700/30 flex items-center gap-3 flex-wrap flex-shrink-0">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, requirement…"
              className="w-full bg-slate-800/60 border border-slate-700/50 rounded-lg text-sm text-white placeholder-slate-500 pl-8 pr-3 py-1.5 focus:outline-none focus:border-indigo-500"
            />
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white hover:text-white text-sm">×</button>
            )}
          </div>
          {/* Status tabs (hidden in bucket drill-down — the bucket already
              defines the filter) */}
          {showStatusTabs && (
            <div className="flex gap-1 flex-wrap">
              {['All', 'Applied', 'Screening', 'Shortlisted', 'Interview', 'Hold', 'Dropped', 'Hired', 'Rejected'].map(s => (
                <button
                  key={s}
                  onClick={() => setCandStatus(s)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                    candStatus === s
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-slate-700/40 border-slate-600/50 text-white hover:text-white'
                  }`}
                >
                  {s === 'All' ? `All (${rec.total})` : `${s} (${rec.byStatus[s] || 0})`}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Table ── */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto overflow-x-auto custom-scroll"
        >
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <div className="text-3xl mb-2">🔍</div>
              <div className="text-sm">No candidates match your filters</div>
            </div>
          ) : (
            <table className="w-full text-xs min-w-[640px]">
              <thead className="sticky top-0 z-10">
                <tr className="text-slate-500 border-b border-slate-700/50 bg-slate-900">
                  <th className="text-left py-2.5 px-2 font-medium whitespace-nowrap w-6" />
                  <th className="text-left py-2.5 px-4 font-medium whitespace-nowrap">#</th>
                  <th className="text-left py-2.5 px-4 font-medium whitespace-nowrap">Candidate</th>
                  <th className="text-left py-2.5 px-4 font-medium whitespace-nowrap">Email</th>
                  <th className="text-left py-2.5 px-4 font-medium whitespace-nowrap">Status</th>
                  <th className="text-left py-2.5 px-4 font-medium whitespace-nowrap">Requirement</th>
                  <th className="text-left py-2.5 px-4 font-medium whitespace-nowrap">Client</th>
                  <th className="text-left py-2.5 px-4 font-medium whitespace-nowrap">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => {
                  // Key on submissionId (unique per row); candidate id can repeat when a
                  // candidate has multiple submissions across requirements. Fall back to
                  // "c.id-i" for legacy payloads that don't yet carry submissionId.
                  const rowKey    = c.submissionId || `${c.id}-${i}`;
                  const isOpen    = c.submissionId && expandedId === c.submissionId;
                  const canExpand = !!c.submissionId;
                  // Ignore clicks that end a text selection (recruiter drag-selecting
                  // a name/email shouldn't toggle the row).
                  const toggle = () => {
                    if (!canExpand) return;
                    if (typeof window !== 'undefined' && window.getSelection && window.getSelection().toString()) return;
                    setExpandedId(isOpen ? null : c.submissionId);
                  };
                  const onKey = (e) => {
                    if (!canExpand) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedId(isOpen ? null : c.submissionId);
                    }
                  };
                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        onClick={toggle}
                        onKeyDown={onKey}
                        role={canExpand ? 'button' : undefined}
                        tabIndex={canExpand ? 0 : -1}
                        aria-expanded={canExpand ? isOpen : undefined}
                        aria-controls={isOpen ? `journey-${c.submissionId}` : undefined}
                        className={`border-b border-slate-700/20 hover:bg-slate-800/40 transition-colors ${canExpand ? 'cursor-pointer' : ''} ${isOpen ? 'bg-slate-800/50' : ''}`}
                      >
                        <td className="py-2.5 px-2 text-slate-500 text-center align-middle" title={canExpand ? 'Show journey' : 'No submission id — journey unavailable'}>
                          <span
                            className="inline-block transition-transform"
                            style={{
                              transform:  isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                              opacity:    canExpand ? 1 : 0.25,
                              fontSize:   11,
                            }}
                            aria-hidden="true"
                          >
                            ▶
                          </span>
                        </td>
                        <td className="py-2.5 px-4 text-slate-500">{i + 1}</td>
                        <td className="py-2.5 px-4">
                          <div className="font-medium text-white truncate max-w-[220px]" title={c.fullName}>{c.fullName}</div>
                          {c.rounds?.length > 0 && <RoundChipStrip rounds={c.rounds} />}
                        </td>
                        <td className="py-2.5 px-4 text-slate-400 max-w-[220px] truncate" title={c.email}>{c.email || '—'}</td>
                        <td className="py-2.5 px-4"><StatusBadge s={c.status} /></td>
                        <td className="py-2.5 px-4 text-slate-300 max-w-[160px] truncate">{c.requirement}</td>
                        <td className="py-2.5 px-4 text-slate-400">{c.client}</td>
                        <td className="py-2.5 px-4 text-slate-500">{fmtDate(c.createdAt)}</td>
                      </tr>
                      {isOpen && (
                        <tr
                          id={`journey-${c.submissionId}`}
                          className="border-b border-slate-700/40"
                          style={{ background: 'var(--bg-surface)' }}
                        >
                          <td />
                          <td colSpan={7} className="py-1 pl-4 pr-6">
                            <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Journey</div>
                            <SubmissionJourney submissionId={c.submissionId} submittedAt={c.createdAt} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Footer count ── */}
        <div className="px-6 py-3 border-t border-slate-700/30 flex items-center justify-between flex-shrink-0">
          <span className="text-slate-500 text-xs">
            Showing <span className="text-white font-medium">{filtered.length}</span> of <span className="text-white font-medium">{rec.total}</span> candidates
          </span>
          <Button variant="outline" size="xs" onClick={onClose}>Close</Button>
        </div>
      </div>

      {/* ── Floating scroll-to-top ── */}
      {showTop && (
        <Button variant="primary" size="sm" iconOnly tooltip="Back to top" onClick={scrollToTop}
          style={{ position: 'fixed', bottom: 32, right: 32, borderRadius: '50%', width: 40, height: 40, zIndex: 10000, boxShadow: '0 4px 14px rgba(99,102,241,0.4)' }}>
          ↑
        </Button>
      )}
    </div>,
    document.body
  );
}

// ── Recruiter card with candidate drill-down ──────────────────────────────────
function RecruiterCard({ rec, idx, period, periodRows, isExpanded, onToggle }) {
  const [modalOpen, setModalOpen]       = useState(false);
  const [modalStatus, setModalStatus]   = useState('All');

  const openModal = (e, status = 'All') => {
    e.stopPropagation();
    setModalStatus(status);
    setModalOpen(true);
  };

  return (
    <>
    <div className="bg-slate-700/30 border border-slate-700/50 rounded-xl overflow-hidden">
      {/* ── Header row — click to expand ── */}
      <div
        className="flex items-start justify-between gap-4 p-4 cursor-pointer hover:bg-slate-700/20 transition-colors select-none flex-wrap"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-full bg-indigo-600/30 border border-indigo-500/30 flex items-center justify-center text-sm font-bold text-indigo-300 flex-shrink-0">
            {idx + 1}
          </div>
          <div className="min-w-0">
            <div className="text-white font-semibold text-sm truncate">{rec.name}</div>
            <div className="text-slate-500 text-xs truncate">{rec.role}</div>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <div className="text-2xl font-bold text-indigo-400">{rec.total}</div>
            <div className="text-slate-500 text-xs">submissions</div>
          </div>
          <span className="text-slate-500 text-sm">{isExpanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* ── Status mini bars (always visible) ── */}
      <div className="px-4 pb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {Object.entries(STATUS_COLORS).map(([s, color]) => (
          <div key={s}>
            <div className="text-xs text-slate-500 mb-0.5">{s}</div>
            <MiniBar value={rec.byStatus[s] || 0} max={rec.total} color={color} />
          </div>
        ))}
      </div>

      {/* ── Expanded panel ── */}
      {isExpanded && (
        <div className="border-t border-slate-700/50 bg-slate-900/40 p-4 space-y-4">

          {/* Period breakdown (only when a period is selected) */}
          {period !== 'total' && periodRows.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                {period === 'byDay' ? 'Daily' : period === 'byWeek' ? 'Weekly' : 'Monthly'} Breakdown
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1 custom-scroll">
                {periodRows.map(row => (
                  <div key={row.key} className="flex items-center justify-between text-xs text-slate-300 py-0.5">
                    <span className="text-slate-400 w-24">{row.key}</span>
                    <div className="flex items-center gap-2 flex-1 ml-3">
                      <div className="flex-1 bg-slate-700 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-indigo-500 transition-all"
                          style={{ width: `${Math.max(4, (row.count / rec.total) * 100)}%` }} />
                      </div>
                      <span className="w-5 text-right font-medium text-white">{row.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Candidates section */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Candidates</div>
            </div>
            {/* Status filter tabs — clicking opens modal pre-filtered */}
            <div className="flex gap-1 flex-wrap mb-3">
              {['All', 'Applied', 'Screening', 'Shortlisted', 'Interview', 'Hold', 'Dropped', 'Hired', 'Rejected'].map(s => (
                <button
                  key={s}
                  onClick={e => openModal(e, s)}
                  className="text-xs px-2 py-0.5 rounded-full border transition-all bg-slate-700/40 border-slate-600/50 text-white hover:bg-indigo-600/20 hover:border-indigo-500/40 hover:text-indigo-300"
                >
                  {s}
                  <span className="ml-1 text-slate-500">
                    {s === 'All' ? rec.total : (rec.byStatus[s] || 0)}
                  </span>
                </button>
              ))}
            </div>
            {/* View all button */}
            <button
              onClick={e => openModal(e, 'All')}
              className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-indigo-600/10 hover:bg-indigo-600/20 border border-indigo-500/20 hover:border-indigo-500/40 transition-all group"
            >
              <div className="flex items-center gap-2">
                <span className="text-indigo-400 text-sm">👥</span>
                <span className="text-sm font-medium text-indigo-300">View All Candidates</span>
                <span className="text-xs bg-indigo-600/30 text-indigo-300 px-2 py-0.5 rounded-full">{rec.total}</span>
              </div>
              <span className="text-indigo-500 group-hover:text-indigo-300 transition-colors text-sm">→</span>
            </button>
          </div>
        </div>
      )}
    </div>

    {/* ── Candidate modal ── */}
    {modalOpen && (
      <RecruiterCandidateModal rec={rec} initialStatus={modalStatus} onClose={() => setModalOpen(false)} />
    )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RecruiterFunnelCard — one card per recruiter for the Interview Funnel tab.
// Layout: header + KPI ribbon on top, hand-drawn funnel bars below (one row
// per stage from Submitted through each round to Hired), and a compact
// round-by-round breakdown table under the funnel.
function RecruiterFunnelCard({ rec }) {
  const [showAllRounds, setShowAllRounds] = useState(false);
  // Bucket drill-down: { predicate, title, subtitle } | null.
  // The predicate is a (candidate) => boolean lambda from FUNNEL_PREDICATES.
  // The subtitle is a human-readable breadcrumb path.
  const [drilldown, setDrilldown] = useState(null);
  const openBucket = (predicate, title, subtitle) =>
    setDrilldown({ predicate, title, subtitle });

  const submitted   = rec.submitted   || 0;
  const interviewed = rec.interviewed || 0;
  const hired       = rec.hired       || 0;
  const roundsAll   = rec.rounds || [];
  const roundsShown = showAllRounds ? roundsAll : roundsAll.slice(0, 5);
  const roundsHidden = roundsAll.length - roundsShown.length;

  // Funnel stages — each carries:
  //  - `prevValue`: for the "-N" drop-off column, stable regardless of the
  //    5-round display cap (Hired compares to `interviewed`, not last visible round)
  //  - `predicate`, `title`, `subtitle`: for the drill-down modal opened by
  //    clicking the bar. Same bucket semantics as the KPI pills / table cells.
  const stages = [];
  stages.push({
    label: 'Submitted', value: submitted, color: '#6366f1', prevValue: null,
    predicate: FUNNEL_PREDICATES.all(), title: `${rec.name} — All submissions`, subtitle: 'Every candidate submitted in this range',
  });
  stages.push({
    label: 'Interviewed', value: interviewed, color: '#3b82f6', prevValue: submitted,
    predicate: FUNNEL_PREDICATES.interviewed(), title: `${rec.name} — Interviewed`, subtitle: 'Candidates with at least one interview round scheduled',
  });
  let prevPassed = interviewed;
  for (const r of roundsShown) {
    stages.push({
      label:     `Round ${r.round} reached`,
      value:     r.reached,
      color:     '#60a5fa',
      subline:   `${r.passed} passed · ${r.failed} failed · ${r.pending} pending`,
      prevValue: prevPassed,
      predicate: FUNNEL_PREDICATES.roundReached(r.round),
      title:     `${rec.name} — R${r.round}+ Reached`,
      subtitle:  `Candidates who reached Round ${r.round} or beyond`,
    });
    stages.push({
      label:     `Round ${r.round} passed`,
      value:     r.passed,
      color:     '#10b981',
      muted:     r.passed === 0,
      prevValue: r.reached,
      predicate: FUNNEL_PREDICATES.roundPassed(r.round),
      title:     `${rec.name} — R${r.round} Passed`,
      subtitle:  `Round ${r.round}: passed`,
    });
    prevPassed = r.passed;
  }
  stages.push({
    label: 'Hired', value: hired, color: '#10b981', prevValue: interviewed,
    predicate: FUNNEL_PREDICATES.hired(), title: `${rec.name} — Hired`, subtitle: 'Candidates who reached Hired status',
  });

  // Adapter — shape passed to the existing RecruiterCandidateModal so the
  // "View interview candidates" button works. `total` uses the recruiter's
  // true submission count (rec.submitted) so the modal header reads correctly,
  // not the length of the interview-track candidates array.
  const adaptedRec = {
    userId: rec.userId,
    name:   rec.name,
    role:   rec.role,
    total:  rec.submitted || 0,
    byStatus: rec.candidates?.reduce((acc, c) => {
      acc[c.status] = (acc[c.status] || 0) + 1;
      return acc;
    }, {}) || {},
    candidates: rec.candidates || [],
  };

  return (
    <Card>
      <div className="space-y-4">
        {/* Header — name + role + KPI ribbon */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{rec.name}</div>
            <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{rec.role}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <FunnelKPI
              label="Submitted"   value={submitted}   color="#6366f1"
              onClick={submitted > 0 ? () => openBucket(FUNNEL_PREDICATES.all(),         `${rec.name} — All submissions`, 'Every candidate submitted in this range') : null}
            />
            <FunnelKPI
              label="Interviewed" value={interviewed} pct={rec.conversion?.subToInterview} color="#3b82f6"
              onClick={interviewed > 0 ? () => openBucket(FUNNEL_PREDICATES.interviewed(), `${rec.name} — Interviewed`, 'Candidates with at least one interview round scheduled') : null}
            />
            <FunnelKPI
              label="Hired"       value={hired}       pct={rec.conversion?.overall}       color="#10b981"
              onClick={hired > 0 ? () => openBucket(FUNNEL_PREDICATES.hired(), `${rec.name} — Hired`, 'Candidates who reached Hired status') : null}
            />
            {rec.rejectedInFinalRound > 0 && (
              <FunnelKPI
                label="Rejected (final)" value={rec.rejectedInFinalRound} color="#ef4444"
                onClick={() => openBucket(FUNNEL_PREDICATES.rejectedFinal(), `${rec.name} — Rejected in final round`, 'Rejected after failing their last interview round')}
              />
            )}
            {rec.rejectedPreInterview > 0 && (
              <FunnelKPI
                label="Rejected (pre)" value={rec.rejectedPreInterview} color="#f87171"
                onClick={() => openBucket(FUNNEL_PREDICATES.rejectedPre(), `${rec.name} — Rejected pre-interview`, 'Rejected before any interview round was scheduled')}
              />
            )}
            {rec.dropped > 0 && (
              <FunnelKPI
                label="Dropped" value={rec.dropped} color="#94a3b8"
                onClick={() => openBucket(FUNNEL_PREDICATES.dropped(), `${rec.name} — Dropped`, 'Candidates who withdrew or lost interest')}
              />
            )}
            {rec.legacyInterviewOnly > 0 && (
              <span
                title="Submissions with status=Interview but no interview-round entries recorded. Legacy data — not counted in round breakdown."
                style={{
                  fontSize: 11, padding: '3px 8px', borderRadius: 99, fontWeight: 600,
                  background: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)',
                }}
              >
                ⚠ {rec.legacyInterviewOnly} legacy
              </span>
            )}
          </div>
        </div>

        {/* Funnel bars — each non-zero bar is a button that opens the drill-down */}
        <div className="space-y-2">
          {stages.map((s) => {
            const w = submitted > 0 ? Math.max(s.value > 0 ? 3 : 0, (s.value / submitted) * 100) : 0;
            const prev = s.prevValue;
            const dropoff = prev !== null && prev > s.value ? prev - s.value : 0;
            const clickable = s.value > 0 && s.predicate;
            // Guard: don't fire when the user is drag-selecting text (e.g. to
            // copy the count into Slack) and don't fire when nothing to show.
            const onClickBar = clickable ? () => {
              if (typeof window !== 'undefined' && window.getSelection && window.getSelection().toString()) return;
              openBucket(s.predicate, s.title, s.subtitle);
            } : undefined;
            return (
              <div key={s.label}>
                <div
                  onClick={onClickBar}
                  onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openBucket(s.predicate, s.title, s.subtitle); } } : undefined}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : -1}
                  title={clickable ? `Click to see the ${s.value === 1 ? '1 candidate' : s.value + ' candidates'} in "${s.label}"` : undefined}
                  className={`flex items-center gap-2 rounded-md transition-colors ${clickable ? 'hover:bg-slate-800/40 cursor-pointer' : ''}`}
                  style={{ padding: clickable ? '2px 4px' : '0', marginLeft: clickable ? '-4px' : '0', marginRight: clickable ? '-4px' : '0' }}
                >
                  <div className="text-xs w-40 flex-shrink-0 truncate" style={{ color: s.muted ? 'var(--text-muted)' : 'var(--text-secondary)' }} title={s.label}>
                    {s.label}
                  </div>
                  <div className="flex-1 relative h-6 rounded-md overflow-hidden" style={{ background: 'var(--border-subtle)' }}>
                    <div
                      className="h-full transition-all rounded-md"
                      style={{
                        width: `${w}%`,
                        background: `linear-gradient(90deg, ${s.color}cc, ${s.color})`,
                        boxShadow: s.value > 0 ? `0 0 8px ${s.color}40` : 'none',
                      }}
                    />
                    <span
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-semibold pointer-events-none"
                      style={{ color: w > 8 ? '#fff' : 'var(--text-secondary)', textDecoration: clickable ? 'underline' : 'none', textDecorationColor: 'rgba(255,255,255,0.35)', textUnderlineOffset: 2 }}
                    >
                      {s.value}
                    </span>
                  </div>
                  {/* Drop-off "-N" — informational only. pointer-events-none so
                      clicks pass through to the bar's own predicate rather than
                      surface a stale "N dropped from previous stage" bucket. */}
                  <div
                    className="text-xs w-20 text-right flex-shrink-0 pointer-events-none"
                    style={{ color: dropoff > 0 ? '#f87171' : 'var(--text-muted)' }}
                    title={dropoff > 0 ? `${dropoff} candidate${dropoff === 1 ? '' : 's'} did not advance from the previous stage` : undefined}
                  >
                    {dropoff > 0 ? `-${dropoff}` : ''}
                  </div>
                </div>
                {s.subline && (
                  <div className="text-[11px] mt-0.5 pl-4 sm:pl-[10.5rem]" style={{ color: 'var(--text-muted)', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {s.subline}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Round-by-round table */}
        {roundsAll.length > 0 && (
          <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-subtle)' }}>
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: 'var(--bg-raised)', color: 'var(--text-muted)' }}>
                  <th className="text-left py-1.5 px-3 font-medium">Round</th>
                  <th className="text-right py-1.5 px-3 font-medium">Reached</th>
                  <th className="text-right py-1.5 px-3 font-medium" style={{ color: '#34d399' }}>Passed</th>
                  <th className="text-right py-1.5 px-3 font-medium" style={{ color: '#f87171' }}>Failed</th>
                  <th className="text-right py-1.5 px-3 font-medium" style={{ color: '#fbbf24' }}>Pending</th>
                  <th className="text-right py-1.5 px-3 font-medium">Pass rate</th>
                </tr>
              </thead>
              <tbody>
                {roundsShown.map(r => {
                  const decided = r.passed + r.failed;
                  const passRate = decided > 0 ? Math.round((r.passed / decided) * 100) : null;
                  // Numeric cell that becomes a button when count > 0.
                  const CountCell = ({ n, colHex, predicate, bucketLabel }) => {
                    if (!n) return <td className="py-1.5 px-3 text-right" style={{ color: colHex, opacity: 0.4 }}>0</td>;
                    return (
                      <td className="py-1.5 px-3 text-right">
                        <button
                          type="button"
                          onClick={() => openBucket(predicate, `${rec.name} — R${r.round} ${bucketLabel}`, `Round ${r.round}: ${bucketLabel.toLowerCase()}`)}
                          className="hover:underline"
                          style={{ color: colHex, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          {n}
                        </button>
                      </td>
                    );
                  };
                  return (
                    <tr key={r.round} style={{ borderTop: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>
                      <td className="py-1.5 px-3 font-medium">R{r.round}</td>
                      <CountCell n={r.reached} colHex="var(--text-secondary)" predicate={FUNNEL_PREDICATES.roundReached(r.round)} bucketLabel="Reached" />
                      <CountCell n={r.passed}  colHex="#34d399"               predicate={FUNNEL_PREDICATES.roundPassed(r.round)}  bucketLabel="Passed" />
                      <CountCell n={r.failed}  colHex="#f87171"               predicate={FUNNEL_PREDICATES.roundFailed(r.round)}  bucketLabel="Failed" />
                      <CountCell n={r.pending} colHex="#fbbf24"               predicate={FUNNEL_PREDICATES.roundPending(r.round)} bucketLabel="Pending" />
                      <td className="py-1.5 px-3 text-right">{passRate === null ? '—' : `${passRate}%`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {roundsHidden > 0 && (
              <button
                onClick={() => setShowAllRounds(true)}
                className="w-full py-1.5 text-xs font-medium hover:underline"
                style={{ color: 'var(--brand-primary)', background: 'var(--bg-raised)', borderTop: '1px solid var(--border-subtle)' }}
              >
                Show {roundsHidden} more round{roundsHidden === 1 ? '' : 's'}
              </button>
            )}
          </div>
        )}

        {/* Actions row */}
        <div className="flex items-center justify-end gap-3 pt-1">
          {interviewed > 0 && (
            <button
              onClick={() => openBucket(FUNNEL_PREDICATES.interviewed(), `${rec.name} — Interviewed`, 'Candidates with at least one interview round scheduled')}
              className="text-xs font-medium hover:underline"
              style={{ color: 'var(--brand-primary)' }}
            >
              View interview candidates →
            </button>
          )}
        </div>
      </div>

      {drilldown && (
        <RecruiterCandidateModal
          rec={adaptedRec}
          filterPredicate={drilldown.predicate}
          title={drilldown.title}
          subtitle={drilldown.subtitle}
          showStatusTabs={false}
          onClose={() => setDrilldown(null)}
        />
      )}
    </Card>
  );
}

// Small pill showing "12 Interviewed 24%" — used in the RecruiterFunnelCard header.
// If onClick is provided the pill becomes an accessible button; hover style
// nudges the border and adds an underline on the value to signal clickability.
function FunnelKPI({ label, value, pct, color, onClick }) {
  const clickable = typeof onClick === 'function';
  const Tag = clickable ? 'button' : 'div';
  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      title={clickable ? `Click to see candidates` : undefined}
      className="flex items-center gap-2 rounded-full transition-all"
      style={{
        padding: '4px 10px',
        background: `${color}18`,
        border: `1px solid ${color}40`,
        cursor: clickable ? 'pointer' : 'default',
      }}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.borderColor = `${color}80`; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.borderColor = `${color}40`; } : undefined}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', textDecoration: clickable ? 'underline' : 'none', textDecorationColor: `${color}66`, textUnderlineOffset: 3 }}>{value}</span>
      {pct != null && (
        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)' }}>{pct}%</span>
      )}
    </Tag>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function Reports() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Recharts needs concrete hex values — it can't read CSS vars.
  const CHART_STATUS_COLORS = isDark ? STATUS_COLORS_DARK : STATUS_COLORS_LIGHT;
  const CHART_PIE_COLORS    = isDark ? PIE_COLORS_DARK    : PIE_COLORS_LIGHT;
  const CHART_GRID     = isDark ? '#334155' : '#e2e8f0';
  const CHART_INK      = isDark ? '#94a3b8' : '#64748b';
  const TOOLTIP_BG     = isDark ? '#1e293b' : '#ffffff';
  const TOOLTIP_BORDER = isDark ? '#334155' : '#e2e8f0';
  const TOOLTIP_FG     = isDark ? '#ffffff' : '#0f172a';

  // ── Date range state ───────────────────────────────────────────────────────
  // The chosen range is kept for this tab. A saved preset ("Last 30 days")
  // re-reads its dates so it never turns into a stale fixed range; only a
  // custom range keeps its saved dates.
  const [activeQuick, setActiveQuick] = usePersistentState('reports:quick', 'Last 30 days');
  const savedPreset = QUICK.find(q => q.label === activeQuick);
  const [from, setFrom] = usePersistentState('reports:from', daysAgo(30), savedPreset ? savedPreset.from : undefined);
  const [to,   setTo]   = usePersistentState('reports:to',   today(),     savedPreset ? savedPreset.to   : undefined);

  // ── Tab ────────────────────────────────────────────────────────────────────
  const [tab, setTab] = usePersistentState('reports:tab', 'submissions');

  // ── Data state ─────────────────────────────────────────────────────────────
  const [loading,  setLoading]  = useState(false);
  const [loaded,   setLoaded]   = useState(false);
  const [subData,  setSubData]  = useState(null);
  const [recData,  setRecData]  = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [funnelRecFilter, setFunnelRecFilter] = useState('all'); // 'all' | recruiter userId

  // Auto-reset the recruiter filter when new funnelData no longer contains
  // the currently-selected userId — prevents an out-of-list value on
  // <select> (React warning) after a date-range change.
  useEffect(() => {
    if (funnelRecFilter === 'all') return;
    const stillExists = (funnelData?.data || [])
      .some(r => String(r.userId) === String(funnelRecFilter));
    if (!stillExists) setFunnelRecFilter('all');
  }, [funnelData, funnelRecFilter]);
  const [convData, setConvData] = useState(null);
  const [convGroupBy, setConvGroupBy] = useState('workMode');

  // ── Period view for recruiter tab ─────────────────────────────────────────
  const [period, setPeriod] = usePersistentState('reports:period', 'total');   // total | byDay | byWeek | byMonth
  const [expandedRec, setExpandedRec] = useState(null);
  const [expandedReq, setExpandedReq] = useState(null);

  // ── All-candidates drawer (KPI click) ─────────────────────────────────────
  const [kpiDrawer,    setKpiDrawer]    = useState(null);  // null | 'all' | 'hired'
  const [kpiCandFilter, setKpiCandFilter] = useState('All');

  // ── Apply date range ───────────────────────────────────────────────────────
  const applyQuick = (q) => {
    setFrom(q.from); setTo(q.to); setActiveQuick(q.label);
  };

  const fetchAll = useCallback(async (f = from, t = to) => {
    setLoading(true);
    try {
      const [s, r, fn, c] = await Promise.all([
        reportAPI.submissions({ from: f, to: t }),
        reportAPI.byRecruiter({ from: f, to: t }),
        reportAPI.recruiterFunnel({ from: f, to: t }),
        reportAPI.conversion({ from: f, to: t, groupBy: convGroupBy }),
      ]);
      setSubData(s.data);
      setRecData(r.data);
      setFunnelData(fn.data);
      setConvData(c.data);
      setLoaded(true);
    } catch (err) {
      // Never leave the previous range's numbers on screen under the newly
      // selected dates — clear them and say the report failed.
      setSubData(null);
      setRecData(null);
      setFunnelData(null);
      setConvData(null);
      setLoaded(false);
      toast.error(err?.response?.data?.message || 'Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [from, to, convGroupBy]); // eslint-disable-line

  const handleGenerate = () => fetchAll(from, to);

  // ── Summary numbers ────────────────────────────────────────────────────────
  const totalSubs    = subData?.total ?? 0;
  const totalReqs    = subData?.data?.length ?? 0;
  const topRecruiter = recData?.data?.[0];
  const hiredTotal   = convData?.data?.reduce((s, d) => s + (d.byStatus?.Hired || 0), 0) ?? 0;
  const convRate     = totalSubs ? pct(hiredTotal, totalSubs) : '—';

  // ── Flat list of all candidates (for KPI drill-down) ─────────────────────
  const allCandidates = (subData?.data || []).flatMap(req =>
    (req.candidates || []).map(c => ({ ...c, requirement: req.jobTitle, client: req.clientName }))
  ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const kpiCandidates = kpiDrawer === 'hired'
    ? allCandidates.filter(c => c.status === 'Hired')
    : kpiCandFilter === 'All'
      ? allCandidates
      : allCandidates.filter(c => c.status === kpiCandFilter);

  // ── Submissions bar-chart data ─────────────────────────────────────────────
  const subChartData = (subData?.data || [])
    .slice(0, 12)
    .map(d => ({ name: d.jobTitle?.length > 22 ? d.jobTitle.slice(0, 22) + '…' : d.jobTitle, ...d.byStatus, total: d.total }));

  // ── Recruiter period table helper ─────────────────────────────────────────
  const getPeriodData = (rec) => {
    if (period === 'total') return [{ key: 'Total', count: rec.total }];
    const src = period === 'byDay' ? rec.byDay : period === 'byWeek' ? rec.byWeek : rec.byMonth;
    return Object.entries(src || {}).sort((a, b) => b[0].localeCompare(a[0]))
      .map(([k, v]) => ({ key: k, count: v }));
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-7xl mx-auto">

      {/* ── Page header ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        paddingBottom: 20, borderBottom: '1px solid var(--border-subtle)',
        flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
            }}>📊</div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Reports</h1>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
            Track submissions, recruiter performance, and conversion rates
          </p>
        </div>
      </div>

      {/* ── Date range picker ── */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 14,
        padding: '16px 20px',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          {/* Quick presets */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {QUICK.map(q => (
              <button key={q.label}
                onClick={() => applyQuick(q)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  border: activeQuick === q.label ? '1px solid #6366f1' : '1px solid var(--border-subtle)',
                  background: activeQuick === q.label
                    ? 'linear-gradient(135deg,#6366f1,#8b5cf6)'
                    : 'var(--bg-raised)',
                  color: activeQuick === q.label ? '#fff' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  boxShadow: activeQuick === q.label ? '0 2px 10px rgba(99,102,241,0.3)' : 'none',
                }}>
                {q.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>From</span>
                <input type="date" value={from} max={to}
                  onChange={e => { setFrom(e.target.value); setActiveQuick('Custom'); }}
                  style={{
                    background: 'var(--bg-input)', border: '1px solid var(--border-input)',
                    color: 'var(--text-primary)', fontSize: 12, borderRadius: 8,
                    padding: '6px 10px', outline: 'none', cursor: 'pointer',
                  }} />
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>→</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>To</span>
                <input type="date" value={to} min={from} max={today()}
                  onChange={e => { setTo(e.target.value); setActiveQuick('Custom'); }}
                  style={{
                    background: 'var(--bg-input)', border: '1px solid var(--border-input)',
                    color: 'var(--text-primary)', fontSize: 12, borderRadius: 8,
                    padding: '6px 10px', outline: 'none', cursor: 'pointer',
                  }} />
              </div>
            </div>
            <Button variant="primary" size="sm" onClick={handleGenerate}
              disabled={loading} loading={loading} loadingText="Loading…">
              Generate
            </Button>
          </div>
        </div>
      </div>

      {/* ── Summary KPIs (shown after load) ── */}
      {loaded && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { id: 'all',   label: 'Total Submissions', value: totalSubs,  icon: '👤', accent: '#6366f1', clickable: true  },
              { id: null,    label: 'Requirements',       value: totalReqs,  icon: '📋', accent: '#3b82f6', clickable: false },
              { id: 'hired', label: 'Hired',              value: hiredTotal, icon: '✅', accent: '#10b981', clickable: true  },
              { id: null,    label: 'Conversion Rate',    value: convRate,   icon: '📈', accent: '#f59e0b', clickable: false },
            ].map(k => (
              <div
                key={k.label}
                onClick={() => {
                  if (!k.clickable) return;
                  if (kpiDrawer === k.id) { setKpiDrawer(null); setKpiCandFilter('All'); }
                  else { setKpiDrawer(k.id); setKpiCandFilter('All'); }
                }}
                style={{
                  background: kpiDrawer === k.id
                    ? `${k.accent}12`
                    : 'var(--bg-card)',
                  border: `1px solid ${kpiDrawer === k.id ? k.accent + '55' : 'var(--border-subtle)'}`,
                  borderRadius: 14,
                  padding: '20px 16px',
                  textAlign: 'center',
                  cursor: k.clickable ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                  userSelect: k.clickable ? 'none' : 'auto',
                  boxShadow: kpiDrawer === k.id ? `0 0 0 3px ${k.accent}22` : 'none',
                }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10, margin: '0 auto 10px',
                  background: k.accent + '18',
                  border: `1px solid ${k.accent}33`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18,
                }}>{k.icon}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: k.accent, lineHeight: 1 }}>{k.value}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 5, fontWeight: 500 }}>{k.label}</div>
                {k.clickable && (
                  <div style={{ color: kpiDrawer === k.id ? k.accent : 'var(--text-muted)', fontSize: 11, marginTop: 6, opacity: 0.8 }}>
                    {kpiDrawer === k.id ? '▲ hide list' : '▼ view list'}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ── KPI candidate drawer ── */}
          {kpiDrawer && (
            <div style={{ background: 'var(--bg-card)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px 12px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <h3 style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14, margin: 0 }}>
                    {kpiDrawer === 'hired' ? '✅ Hired Candidates' : '👤 All Submitted Candidates'}
                  </h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 3 }}>
                    {fmtDate(subData?.from)} – {fmtDate(subData?.to)} · {kpiCandidates.length} candidates
                  </p>
                </div>

                {/* Status filter tabs (only for 'all' drawer) */}
                {kpiDrawer === 'all' && (
                  <div className="flex gap-1 flex-wrap">
                    {['All', 'Applied', 'Screening', 'Shortlisted', 'Interview', 'Hold', 'Dropped', 'Hired', 'Rejected'].map(s => {
                      const cnt = s === 'All' ? allCandidates.length : allCandidates.filter(c => c.status === s).length;
                      return (
                        <button key={s} onClick={() => setKpiCandFilter(s)}
                          style={{
                            fontSize: 11, padding: '3px 10px', borderRadius: 99, cursor: 'pointer', fontWeight: 600,
                            border: kpiCandFilter === s ? '1px solid #6366f1' : '1px solid var(--border-subtle)',
                            background: kpiCandFilter === s ? '#6366f1' : 'var(--bg-raised)',
                            color: kpiCandFilter === s ? '#fff' : 'var(--text-secondary)',
                            transition: 'all 0.15s',
                          }}>
                          {s} <span className="opacity-60">{cnt}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <PrintButton
                    label="Print Report"
                    onClick={() => printCandidateList({
                      title:     kpiDrawer === 'hired' ? 'Hired Candidates Report' : 'All Submissions Report',
                      subtitle:  kpiCandFilter !== 'All' ? `Status: ${kpiCandFilter}` : undefined,
                      dateRange: `${fmtDate(subData?.from)} – ${fmtDate(subData?.to)}`,
                      columns: [
                        { label: '#',            key: '_idx',        width: '40px'  },
                        { label: 'Candidate',    key: 'fullName'                    },
                        { label: 'Status',       key: 'status',      width: '110px' },
                        { label: 'Requirement',  key: 'requirement'                 },
                        { label: 'Client',       key: 'client',      width: '160px' },
                        { label: 'Submitted By', key: 'submittedBy', width: '140px' },
                        { label: 'Date',         key: '_date',       width: '110px' },
                      ],
                      rows: kpiCandidates.map((c, i) => ({
                        ...c, _idx: i + 1, _date: fmtDate(c.createdAt),
                      })),
                    })}
                  />
                  <Button variant="ghost" size="sm" iconOnly onClick={() => { setKpiDrawer(null); setKpiCandFilter('All'); }}>✕</Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                {kpiCandidates.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-10">No candidates found.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-raised)' }}>
                        {['#','Candidate','Status','Requirement','Client','Submitted By','Date'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '8px 16px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {kpiCandidates.map((c, i) => (
                        <tr key={c.id} style={{ borderBottom: '1px solid var(--border-subtle)', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--bg-raised)'}
                          onMouseLeave={e => e.currentTarget.style.background=''}
                        >
                          <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-muted)' }}>{i + 1}</td>
                          <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.fullName}</td>
                          <td style={{ padding: '10px 16px' }}><StatusBadge s={c.status} /></td>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-secondary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.requirement}</td>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-secondary)' }}>{c.client}</td>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-secondary)' }}>{c.submittedBy}</td>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(c.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {!loaded && !loading && (
        <div style={{ textAlign: 'center', padding: '64px 0' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16, margin: '0 auto 16px',
            background: 'rgba(99,102,241,0.1)',
            border: '1px solid rgba(99,102,241,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28,
          }}>📊</div>
          <p style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>Ready to generate</p>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
            Select a date range and click <strong style={{ color: '#6366f1' }}>Generate</strong> to see your reports
          </p>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-16"><Spinner /></div>
      )}

      {/* ── Tabs ── */}
      {loaded && !loading && (
        <>
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <div style={{
              display: 'flex', gap: 4,
              background: 'var(--bg-raised)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 12, padding: 4, width: 'fit-content',
              flexWrap: 'nowrap',
            }}>
              {[
                { id: 'submissions', label: '📋 Submissions' },
                { id: 'recruiter',   label: '👤 Per Recruiter' },
                { id: 'funnel',      label: '🎯 Interview Funnel' },
                { id: 'conversion',  label: '📈 Conversion Rate' },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  style={{
                    padding: '8px 18px',
                    borderRadius: 9,
                    fontSize: 13,
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    background: tab === t.id
                      ? 'linear-gradient(135deg,#6366f1,#8b5cf6)'
                      : 'transparent',
                    color: tab === t.id ? '#fff' : 'var(--text-secondary)',
                    boxShadow: tab === t.id ? '0 2px 10px rgba(99,102,241,0.3)' : 'none',
                  }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════════════
              TAB 1 — SUBMISSIONS PER REQUIREMENT
          ════════════════════════════════════════════════════════════════ */}
          {tab === 'submissions' && (
            <div className="space-y-5">
              {/* Bar chart */}
              {subChartData.length > 0 && (
                <Card title="Candidate Submissions by Requirement" sub={`${fmtDate(subData.from)} – ${fmtDate(subData.to)}`}>
                  {/* Legend pills */}
                  <div className="flex flex-wrap gap-3 mb-5">
                    {Object.entries(CHART_STATUS_COLORS).map(([status, color]) => {
                      const total = subChartData.reduce((s, d) => s + (d[status] || 0), 0);
                      return (
                        <div key={status} className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                          style={{ background: color + '18', border: `1px solid ${color}40` }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color }}>{status}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: TOOLTIP_FG }}>{total}</span>
                        </div>
                      );
                    })}
                  </div>
                  <ResponsiveContainer width="100%" height={Math.max(280, subChartData.length * 42)}>
                    <BarChart data={subChartData} layout="vertical"
                      margin={{ left: 8, right: 48, top: 4, bottom: 4 }} barCategoryGap="25%">
                      <defs>
                        {Object.entries(CHART_STATUS_COLORS).map(([status, color]) => (
                          <linearGradient key={status} id={`grad-${status}`} x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                            <stop offset="100%" stopColor={color} stopOpacity={1} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} horizontal={false} />
                      <XAxis type="number" tick={{ fill: CHART_INK, fontSize: 11 }}
                        axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={145}
                        tick={{ fill: CHART_INK, fontSize: 11, fontWeight: 500 }}
                        axisLine={false} tickLine={false} dx={-4} />
                      <Tooltip
                        cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', radius: 4 }}
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const total = payload.reduce((s, p) => s + (p.value || 0), 0);
                          return (
                            <div style={{
                              background: TOOLTIP_BG, border: `1px solid ${TOOLTIP_BORDER}`,
                              borderRadius: 12, padding: '12px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                              minWidth: 180,
                            }}>
                              <p style={{ fontSize: 13, fontWeight: 700, color: TOOLTIP_FG, marginBottom: 10 }}>{label}</p>
                              {payload.map(p => p.value > 0 && (
                                <div key={p.name} className="flex items-center justify-between gap-4" style={{ marginBottom: 6 }}>
                                  <div className="flex items-center gap-2">
                                    <span style={{ width: 10, height: 10, borderRadius: 3, background: p.fill, display: 'inline-block' }} />
                                    <span style={{ fontSize: 12, color: CHART_INK }}>{p.name}</span>
                                  </div>
                                  <span style={{ fontSize: 13, fontWeight: 700, color: p.fill }}>{p.value}</span>
                                </div>
                              ))}
                              <div style={{ borderTop: `1px solid ${TOOLTIP_BORDER}`, marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: 12, color: CHART_INK }}>Total</span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: TOOLTIP_FG }}>{total}</span>
                              </div>
                            </div>
                          );
                        }}
                      />
                      {Object.entries(CHART_STATUS_COLORS).map(([status, color], i, arr) => (
                        <Bar key={status} dataKey={status} stackId="a"
                          fill={`url(#grad-${status})`}
                          radius={i === arr.length - 1 ? [0, 6, 6, 0] : [0, 0, 0, 0]}
                          isAnimationActive={true} animationDuration={700} animationEasing="ease-out"
                          animationBegin={i * 60}>
                          {i === arr.length - 1 && (
                            <LabelList dataKey="total" position="right"
                              style={{ fill: CHART_INK, fontSize: 11, fontWeight: 700 }} />
                          )}
                        </Bar>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Table */}
              <Card title="Requirement Breakdown">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 text-xs border-b border-slate-700">
                        <th className="text-left py-2 pr-4">Requirement</th>
                        <th className="text-left py-2 pr-4">Client</th>
                        <th className="text-left py-2 pr-4">Type</th>
                        <th className="text-center py-2 px-2">Total</th>
                        {['Applied','Screening','Shortlisted','Interview','Hold','Dropped','Hired','Rejected'].map(s => (
                          <th key={s} className="text-center py-2 px-2">{s}</th>
                        ))}
                        <th className="text-center py-2 pl-2">Hired%</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {(subData?.data || []).map(row => (
                        <React.Fragment key={row.requirementId}>
                          <tr
                            className={`border-b border-slate-700/40 cursor-pointer transition-colors ${
                              expandedReq === row.requirementId ? 'bg-slate-700/30' : 'hover:bg-slate-700/20'
                            }`}
                            onClick={() => setExpandedReq(expandedReq === row.requirementId ? null : row.requirementId)}
                          >
                            <td className="py-2.5 pr-4">
                              <div className="flex items-center gap-1.5">
                                <span className="text-slate-500 text-xs">{expandedReq === row.requirementId ? '▼' : '▶'}</span>
                                <span className="text-white font-medium">{row.jobTitle}</span>
                              </div>
                            </td>
                            <td className="py-2.5 pr-4 text-slate-300">{row.clientName}</td>
                            <td className="py-2.5 pr-4">
                              <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">{row.workMode}</span>
                            </td>
                            <td className="py-2.5 px-2 text-center font-bold text-indigo-400">{row.total}</td>
                            {['Applied','Screening','Shortlisted','Interview','Hold','Dropped','Hired','Rejected'].map(s => (
                              <td key={s} className="py-2.5 px-2 text-center text-slate-300">{row.byStatus[s] || 0}</td>
                            ))}
                            <td className="py-2.5 pl-2 text-center text-emerald-400 font-medium">
                              {pct(row.byStatus.Hired || 0, row.total)}
                            </td>
                            <td className="py-2.5 pl-2 text-center">
                              <span className={`text-xs font-medium ${expandedReq === row.requirementId ? 'text-indigo-300' : 'text-indigo-400'}`}>
                                {expandedReq === row.requirementId ? 'Hide ▲' : 'Details ▼'}
                              </span>
                            </td>
                          </tr>
                          {expandedReq === row.requirementId && (
                            <RequirementCandidatesRow row={row} colSpan={10} />
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  {!subData?.data?.length && (
                    <p className="text-center text-slate-500 py-8 text-sm">No submissions found for this period.</p>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              TAB 2 — PER RECRUITER
          ════════════════════════════════════════════════════════════════ */}
          {tab === 'recruiter' && (
            <div className="space-y-5">
              {/* Bar chart — total per recruiter */}
              {(recData?.data || []).length > 0 && (
                <Card title="Submissions per Recruiter" sub={`${fmtDate(recData.from)} – ${fmtDate(recData.to)}`}>
                  <div className="flex flex-wrap gap-3 mb-5">
                    {Object.entries(CHART_STATUS_COLORS).map(([status, color]) => {
                      const total = (recData?.data || []).reduce((s, r) => s + (r.byStatus?.[status] || 0), 0);
                      return (
                        <div key={status} className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                          style={{ background: color + '18', border: `1px solid ${color}40` }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color }}>{status}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: TOOLTIP_FG }}>{total}</span>
                        </div>
                      );
                    })}
                  </div>
                  <ResponsiveContainer width="100%" height={Math.max(240, Math.min(10, (recData?.data || []).length) * 40)}>
                    <BarChart
                      data={(recData?.data || []).slice(0,10).map(r => ({ name: r.name.split(' ')[0], ...r.byStatus, total: r.total }))}
                      layout="vertical" margin={{ left: 8, right: 52, top: 4, bottom: 4 }} barCategoryGap="25%">
                      <defs>
                        {Object.entries(CHART_STATUS_COLORS).map(([status, color]) => (
                          <linearGradient key={status} id={`rgrad-${status}`} x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                            <stop offset="100%" stopColor={color} stopOpacity={1} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} horizontal={false} />
                      <XAxis type="number" tick={{ fill: CHART_INK, fontSize: 11 }}
                        axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={80}
                        tick={{ fill: CHART_INK, fontSize: 11, fontWeight: 600 }}
                        axisLine={false} tickLine={false} dx={-4} />
                      <Tooltip
                        cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', radius: 4 }}
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const total = payload.reduce((s, p) => s + (p.value || 0), 0);
                          return (
                            <div style={{
                              background: TOOLTIP_BG, border: `1px solid ${TOOLTIP_BORDER}`,
                              borderRadius: 12, padding: '12px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: 180,
                            }}>
                              <p style={{ fontSize: 13, fontWeight: 700, color: TOOLTIP_FG, marginBottom: 10 }}>{label}</p>
                              {payload.map(p => p.value > 0 && (
                                <div key={p.name} className="flex items-center justify-between gap-4" style={{ marginBottom: 6 }}>
                                  <div className="flex items-center gap-2">
                                    <span style={{ width: 10, height: 10, borderRadius: 3, background: p.fill, display: 'inline-block' }} />
                                    <span style={{ fontSize: 12, color: CHART_INK }}>{p.name}</span>
                                  </div>
                                  <span style={{ fontSize: 13, fontWeight: 700, color: p.fill }}>{p.value}</span>
                                </div>
                              ))}
                              <div style={{ borderTop: `1px solid ${TOOLTIP_BORDER}`, marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: 12, color: CHART_INK }}>Total</span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: TOOLTIP_FG }}>{total}</span>
                              </div>
                            </div>
                          );
                        }}
                      />
                      {Object.entries(CHART_STATUS_COLORS).map(([status, color], i, arr) => (
                        <Bar key={status} dataKey={status} stackId="a"
                          fill={`url(#rgrad-${status})`}
                          radius={i === arr.length - 1 ? [0, 6, 6, 0] : [0, 0, 0, 0]}
                          isAnimationActive={true} animationDuration={700} animationEasing="ease-out"
                          animationBegin={i * 60}>
                          {i === arr.length - 1 && (
                            <LabelList dataKey="total" position="right"
                              style={{ fill: CHART_INK, fontSize: 11, fontWeight: 700 }} />
                          )}
                        </Bar>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Leaderboard rows */}
                  <div style={{ marginTop: 20, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}`, paddingTop: 16 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: CHART_INK, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>
                      Leaderboard
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(recData?.data || []).slice(0, 10).map((rec, idx) => {
                        const maxTotal = (recData?.data?.[0]?.total || 1);
                        const pctWidth = Math.round((rec.total / maxTotal) * 100);
                        const medals = ['🥇', '🥈', '🥉'];
                        const hiredCount = rec.byStatus?.Hired || 0;
                        const hireRate = rec.total ? ((hiredCount / rec.total) * 100).toFixed(0) : 0;
                        return (
                          <div key={rec.id || rec.name} style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '8px 12px',
                            flexWrap: 'wrap',
                            background: idx === 0
                              ? (isDark ? 'rgba(99,102,241,0.08)' : '#f5f3ff')
                              : (isDark ? 'rgba(255,255,255,0.02)' : '#fafafa'),
                            borderRadius: 10,
                            border: `1px solid ${idx === 0
                              ? (isDark ? 'rgba(99,102,241,0.2)' : '#e0e7ff')
                              : (isDark ? 'rgba(255,255,255,0.05)' : '#f0f0f0')}`,
                          }}>
                            <span style={{ fontSize: idx < 3 ? 18 : 12, width: 24, textAlign: 'center', fontWeight: 700, color: CHART_INK, flexShrink: 0 }}>
                              {idx < 3 ? medals[idx] : `#${idx + 1}`}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: TOOLTIP_FG, minWidth: 80, flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {rec.name?.split(' ')[0]}
                            </span>
                            <div style={{ flex: 1, height: 6, background: isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                              <div style={{
                                height: '100%', borderRadius: 99,
                                width: `${pctWidth}%`,
                                background: `linear-gradient(to right, #6366f1, #8b5cf6)`,
                                transition: 'width 0.8s ease',
                              }} />
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 700, color: TOOLTIP_FG, minWidth: 28, textAlign: 'right', flexShrink: 0 }}>
                              {rec.total}
                            </span>
                            <span style={{
                              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, flexShrink: 0,
                              background: hireRate >= 20 ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.15)',
                              color: hireRate >= 20 ? (isDark ? '#6ee7b7' : '#059669') : CHART_INK,
                              border: `1px solid ${hireRate >= 20 ? 'rgba(16,185,129,0.3)' : 'rgba(100,116,139,0.2)'}`,
                            }}>
                              {hireRate}% hired
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Card>
              )}

              {/* Recruiter cards */}
              <Card
                title="Recruiter Activity"
                headerRight={
                  <div className="flex gap-1.5 bg-slate-800 border border-slate-700 rounded-lg p-1">
                    {[
                      { id: 'total',   label: 'Total'     },
                      { id: 'byDay',   label: 'Per Day'   },
                      { id: 'byWeek',  label: 'Per Week'  },
                      { id: 'byMonth', label: 'Per Month' },
                    ].map(p => (
                      <button key={p.id} onClick={() => setPeriod(p.id)}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                          period === p.id
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-white hover:text-white hover:bg-slate-700/60'
                        }`}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                }
              >
                <div className="space-y-3">
                  {(recData?.data || []).map((rec, idx) => {
                    const periodRows = getPeriodData(rec);
                    const isExpanded = expandedRec === rec.userId;
                    return (
                      <RecruiterCard
                        key={rec.userId}
                        rec={rec}
                        idx={idx}
                        period={period}
                        periodRows={periodRows}
                        isExpanded={isExpanded}
                        onToggle={() => setExpandedRec(isExpanded ? null : rec.userId)}
                      />
                    );
                  })}
                  {!recData?.data?.length && (
                    <p className="text-center text-slate-500 py-8 text-sm">No submissions found for this period.</p>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              TAB 3 — INTERVIEW FUNNEL
          ════════════════════════════════════════════════════════════════ */}
          {tab === 'funnel' && (() => {
            const allFunnelRecs = funnelData?.data || [];
            const visibleFunnelRecs = funnelRecFilter === 'all'
              ? allFunnelRecs
              : allFunnelRecs.filter(r => String(r.userId) === String(funnelRecFilter));
            const selectedRec = funnelRecFilter === 'all' ? null : allFunnelRecs.find(r => String(r.userId) === String(funnelRecFilter));
            return (
              <div className="space-y-4">
                {allFunnelRecs.length === 0 ? (
                  <Card>
                    <div className="flex flex-col items-center justify-center py-16" style={{ color: 'var(--text-muted)' }}>
                      <div className="text-4xl mb-2">🎯</div>
                      <div className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>No submissions in this range</div>
                      <div className="text-xs">Pick a wider date range or wait for submissions to come in.</div>
                    </div>
                  </Card>
                ) : (
                  <>
                    {/* Filter row — recruiter selector + info line */}
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="text-xs flex-1 min-w-[240px]" style={{ color: 'var(--text-muted)' }}>
                        Interviewed is measured by the number of interview rounds actually scheduled on each submission — not the status alone.
                        Legacy submissions with the Interview status but no rounds are surfaced separately in each card.
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto sm:min-w-[260px]">
                        <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Recruiter:</label>
                        <div className="flex-1 min-w-0">
                          <SearchableSelect
                            value={funnelRecFilter === 'all' ? '' : String(funnelRecFilter)}
                            onChange={v => setFunnelRecFilter(v || 'all')}
                            placeholder={`— All recruiters (${allFunnelRecs.length}) —`}
                            options={allFunnelRecs.map(r => ({
                              value: String(r.userId),
                              label: r.name,
                              sub:   `${r.role} · ${r.submitted} submitted`,
                            }))}
                          />
                        </div>
                      </div>
                    </div>

                    {visibleFunnelRecs.length === 0 ? (
                      <Card>
                        <div className="flex flex-col items-center justify-center py-10 gap-2" style={{ color: 'var(--text-muted)' }}>
                          <div className="text-sm">
                            {selectedRec ? `${selectedRec.name} has` : 'This recruiter has'} no submissions in the selected range.
                          </div>
                          <button
                            onClick={() => setFunnelRecFilter('all')}
                            className="text-xs font-medium hover:underline"
                            style={{ color: 'var(--brand-primary)' }}
                          >
                            Show all recruiters →
                          </button>
                        </div>
                      </Card>
                    ) : (
                      visibleFunnelRecs.map((rec) => (
                        <RecruiterFunnelCard key={rec.userId} rec={rec} />
                      ))
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* ════════════════════════════════════════════════════════════════
              TAB 4 — CONVERSION RATE
          ════════════════════════════════════════════════════════════════ */}
          {tab === 'conversion' && (
            <div className="space-y-5">
              {/* Group-by toggle */}
              <div className="flex items-center gap-3">
                <span className="text-slate-400 text-sm">Group by:</span>
                {[
                  { id: 'workMode', label: 'Work Mode'  },
                  { id: 'jobType',  label: 'Job Type'   },
                ].map(g => (
                  <button key={g.id}
                    onClick={async () => {
                      setConvGroupBy(g.id);
                      setLoading(true);
                      try {
                        const { data } = await reportAPI.conversion({ from, to, groupBy: g.id });
                        setConvData(data);
                      } finally { setLoading(false); }
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      convGroupBy === g.id
                        ? 'bg-indigo-600 border-indigo-500 text-white'
                        : 'bg-slate-500/60 border-slate-500 text-white hover:bg-slate-600'
                    }`}>
                    {g.label}
                  </button>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                {/* Pie — total distribution */}
                <Card title="Candidate Distribution" sub={`by ${convGroupBy === 'workMode' ? 'Work Mode' : 'Job Type'}`}>
                  {(convData?.data || []).length ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={(convData?.data || []).map(d => ({ name: d.type, value: d.total }))}
                          cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}
                          labelLine={false}>
                          {(convData?.data || []).map((_, i) => <Cell key={i} fill={CHART_PIE_COLORS[i % CHART_PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: TOOLTIP_BG, border: `1px solid ${TOOLTIP_BORDER}`, borderRadius: 8, color: TOOLTIP_FG }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-slate-500 text-sm text-center py-8">No data</p>}
                </Card>

                {/* Bar — hire rate per type */}
                <Card title="Hire Rate %" sub="Percentage of candidates reaching Hired status">
                  {(convData?.data || []).length ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={(convData?.data || []).map(d => ({ name: d.type, 'Hire Rate %': d.hiredRate, 'Interview %': d.interviewRate }))} margin={{ left: -10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                        <XAxis dataKey="name" tick={{ fill: CHART_INK, fontSize: 11 }} />
                        <YAxis tick={{ fill: CHART_INK, fontSize: 11 }} unit="%" />
                        <Tooltip contentStyle={{ background: TOOLTIP_BG, border: `1px solid ${TOOLTIP_BORDER}`, borderRadius: 8, color: TOOLTIP_FG }} formatter={(v) => `${v}%`} />
                        <Legend wrapperStyle={{ fontSize: 12, color: CHART_INK }} />
                        <Bar dataKey="Hire Rate %" fill={CHART_STATUS_COLORS.Hired}     radius={[4,4,0,0]} />
                        <Bar dataKey="Interview %" fill={CHART_STATUS_COLORS.Interview} radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-slate-500 text-sm text-center py-8">No data</p>}
                </Card>
              </div>

              {/* Detail table */}
              <Card title="Conversion Details">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 text-xs border-b border-slate-700">
                        <th className="text-left py-2 pr-4">{convGroupBy === 'workMode' ? 'Work Mode' : 'Job Type'}</th>
                        <th className="text-center py-2 px-3">Total</th>
                        {['Applied','Screening','Shortlisted','Interview','Hold','Dropped','Hired','Rejected'].map(s => (
                          <th key={s} className="text-center py-2 px-3">{s}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(convData?.data || []).map(d => (
                        <tr key={d.type} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors last:border-0">
                          <td className="py-2 pr-4 text-white font-medium text-sm">{d.type}</td>
                          <td className="py-2 px-3 text-center text-white font-semibold">{d.total}</td>
                          {['Applied','Screening','Shortlisted','Interview','Hold','Dropped','Hired','Rejected'].map(s => (
                            <td key={s} className="py-2 px-3 text-center text-slate-300">{d.byStatus?.[s] || 0}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}

